// src/components/admin/UserManagementPanel.js
import React, { useState, useEffect } from 'react';

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  // Fetch users data with the fetching logic inside useEffect
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let url = `/api/admin/users?page=${currentPage}`;
        if (searchTerm) {
          url += `&search=${encodeURIComponent(searchTerm)}`;
        }
        
        const response = await fetch(url, {
          credentials: 'include',
        });
        
        if (response.status === 403) {
          throw new Error('Unauthorized: Admin access required');
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.totalPages);
        setCurrentPage(data.page);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, searchTerm]); // Include dependencies the fetch relies on

  // Handle admin status toggle
  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      setIsUpdating(true);
      const response = await fetch(`/api/admin/users/${userId}/toggle-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isAdmin: !currentStatus }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user admin status');
      }
      
      // Update the local state to reflect the change
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, is_admin: !currentStatus } 
            : user
        )
      );
      
      setUpdateMessage({
        type: 'success',
        text: `User admin status updated successfully`
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (err) {
      console.error('Error updating admin status:', err);
      setUpdateMessage({
        type: 'error',
        text: err.message || 'Error updating user admin status'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when performing a new search
  };

  // Handle page change
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-blue-300">User Management</h2>
        
        <div className="flex">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email"
              className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Search
            </button>
          </form>
          
          <button
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            className="ml-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
          >
            Reset
          </button>
        </div>
      </div>
      
      {/* Status message */}
      {updateMessage && (
        <div className={`mb-4 p-3 rounded-md ${
          updateMessage.type === 'success' 
            ? 'bg-green-900/30 border border-green-700 text-green-200' 
            : 'bg-red-900/30 border border-red-700 text-red-200'
        }`}>
          {updateMessage.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-6">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-2">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200">
          <p>{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">ID</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Username</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Email</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Created</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Last Login</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Admin</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-750">
                      <td className="py-3 px-4 text-gray-300">{user.id}</td>
                      <td className="py-3 px-4 text-gray-300">{user.username}</td>
                      <td className="py-3 px-4 text-gray-300">{user.email}</td>
                      <td className="py-3 px-4 text-gray-300">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleDateString() 
                          : 'Never'
                        }
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.is_admin 
                            ? 'bg-green-900/30 text-green-300' 
                            : 'bg-gray-700 text-gray-300'
                        }`}>
                          {user.is_admin ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => toggleAdminStatus(user.id, user.is_admin)}
                          disabled={isUpdating}
                          className={`px-3 py-1 rounded-md text-sm ${
                            user.is_admin
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center py-4 px-4 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Showing page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => goToPage(currentPage > 1 ? currentPage - 1 : 1)}
                    disabled={currentPage <= 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage <= 1
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => goToPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    disabled={currentPage >= totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage >= totalPages
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagementPanel;